<template>
  <div v-if="displayedEvents.length > 0" class="v2-hero-events">
    <p class="v2-hero-events__eyebrow">{{ eyebrowLabel }}</p>
    <div class="v2-hero-events__grid">
      <a
        v-for="ev in displayedEvents"
        :key="ev.id"
        :href="eventLink(ev)"
        target="_blank"
        rel="noopener"
        class="v2-hero-events__card"
        :class="{ 'v2-hero-events__card--past': ev.__past }"
        @mouseenter="onHover(ev, $event)"
        @mouseleave="onHoverEnd"
        @focusin="onHover(ev, $event)"
        @focusout="onHoverEnd"
      >
        <div class="v2-hero-events__card-date">
          <span class="v2-hero-events__card-month">{{ formatMonth(ev.begins_at) }}</span>
          <span class="v2-hero-events__card-day">{{ formatDay(ev.begins_at) }}</span>
        </div>
        <div class="v2-hero-events__card-content">
          <h3 class="v2-hero-events__card-title">{{ ev.title }}</h3>
          <div v-if="formatTime(ev.begins_at)" class="v2-hero-events__card-time">
            <svg class="v2-hero-events__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span>{{ formatTime(ev.begins_at) }}</span>
          </div>
          <span v-if="ev.googleMapUrl" class="v2-hero-events__card-dirs">
            <svg class="v2-hero-events__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <span>Directions</span>
          </span>
        </div>
      </a>
    </div>
    <a
      v-if="hiddenCount > 0"
      href="#activity"
      class="v2-hero-events__more"
    >
      + {{ hiddenCount }} more
    </a>

    <Teleport to="body">
      <div
        v-show="hoveredEvent"
        class="v2-hero-events-tip"
        :class="{ 'is-open': tipOpen }"
        :style="tipStyle"
        role="tooltip"
        aria-hidden="true"
      >
        <p class="v2-hero-events-tip__title">{{ hoveredEvent && hoveredEvent.title }}</p>
        <p class="v2-hero-events-tip__date">{{ tooltipDate }}</p>
        <p v-if="tooltipVenue" class="v2-hero-events-tip__venue">{{ tooltipVenue }}</p>
      </div>
    </Teleport>
  </div>
</template>

<script>
/**
 * V2HeroEvents — frosted-glass event cards under the hero copy.
 *
 * Each card renders as a horizontal pill (date column + content column) with
 * the surrounding hero photo visible through a translucent white panel. On
 * hover/focus a light frosted-glass tooltip portals to the body, surfacing the
 * full untruncated title, a full date+time, and venue — useful when the card
 * title clips with ellipsis. Past events dim to 0.55 opacity and don't lift on
 * hover but remain clickable (the Directions link still resolves to the venue
 * on Google Maps).
 *
 * Silent when the campaign has no events with a begins_at date — the whole
 * block doesn't render and takes no layout space.
 */
const MAX_CARDS = 3
const TIP_FADE_MS = 200

export default {
  name: 'V2HeroEvents',
  props: {
    campaignId: { type: [Number, String], required: true },
  },
  data() {
    return {
      events: [],
      hoveredEvent: null,
      tipOpen: false,
      tipStyle: { left: '0px', top: '0px' },
      tipHideTimer: null,
    }
  },
  computed: {
    displayedEvents() {
      const now = Date.now()
      const tagged = this.events
        .filter(ev => ev.begins_at)
        .map(ev => ({
          ...ev,
          __past: new Date(ev.begins_at).getTime() < now,
        }))
      const upcoming = tagged
        .filter(ev => !ev.__past)
        .sort((a, b) => new Date(a.begins_at).getTime() - new Date(b.begins_at).getTime())
      const past = tagged
        .filter(ev => ev.__past)
        .sort((a, b) => new Date(b.begins_at).getTime() - new Date(a.begins_at).getTime())
      return [...upcoming, ...past].slice(0, MAX_CARDS)
    },
    eyebrowLabel() {
      return this.displayedEvents.some(ev => !ev.__past) ? 'Upcoming Events' : 'Events'
    },
    hiddenCount() {
      const total = this.events.filter(ev => ev.begins_at).length
      return Math.max(0, total - MAX_CARDS)
    },
    tooltipDate() {
      if (!this.hoveredEvent) return ''
      const longDate = this.formatLongDate(this.hoveredEvent.begins_at)
      const time = this.formatTime(this.hoveredEvent.begins_at)
      return time ? `${longDate} · ${time}` : longDate
    },
    tooltipVenue() {
      const ev = this.hoveredEvent
      if (!ev) return ''
      if (ev.address) return ev.address
      if (ev.city && ev.state) return `${ev.city}, ${ev.state}`
      return ev.city || ''
    },
  },
  created() {
    if (!this.campaignId) return
    axios
      .get(`/campaign/${this.campaignId}/events`)
      .then((response) => {
        this.events = Array.isArray(response.data) ? response.data : []
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('V2HeroEvents: failed to load events', err)
      })
  },
  beforeUnmount() {
    if (this.tipHideTimer) clearTimeout(this.tipHideTimer)
  },
  methods: {
    formatMonth(iso) {
      if (!iso) return ''
      return new Date(iso).toLocaleString('en-US', { month: 'short' }).toUpperCase()
    },
    formatDay(iso) {
      if (!iso) return ''
      return new Date(iso).getDate()
    },
    formatTime(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      // Midnight on a date-only event means "no time set" — skip the time row.
      if (d.getHours() === 0 && d.getMinutes() === 0) return ''
      return d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    },
    formatLongDate(iso) {
      if (!iso) return ''
      return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric' })
    },
    onHover(ev, evt) {
      clearTimeout(this.tipHideTimer)
      this.hoveredEvent = ev
      const card = evt.currentTarget
      const rect = card.getBoundingClientRect()
      // The tip is teleported to <body> and positioned `absolute`, i.e.
      // relative to the *document*. getBoundingClientRect() is viewport-
      // relative, so we add window.scrollX/Y to convert it into document
      // coordinates. This anchors the tip to the card's position on the page
      // so it scrolls *with* the card — rather than hanging in the viewport
      // (position: fixed) and detaching from the event as you scroll.
      this.tipStyle = {
        left: `${rect.left + window.scrollX + 12}px`,
        top: `${rect.bottom + window.scrollY + 8}px`,
      }
      requestAnimationFrame(() => { this.tipOpen = true })
    },
    onHoverEnd() {
      this.tipOpen = false
      clearTimeout(this.tipHideTimer)
      this.tipHideTimer = setTimeout(() => { this.hoveredEvent = null }, TIP_FADE_MS)
    },
    eventLink(ev) {
      // /event/{id} doesn't exist (no show method on EventsController), so
      // each card opens the venue in Google Maps when an address is present;
      // otherwise it falls back to the campaign's activity anchor.
      const hasAddress = ev.address || ev.city || ev.state || ev.zip
      return hasAddress && ev.googleMapUrl ? ev.googleMapUrl : '#activity'
    },
  },
}
</script>

<style scoped>
.v2-hero-events {
  margin-top: 24px;
  font-family: 'Red Hat Display', system-ui, sans-serif;
}

.v2-hero-events__eyebrow {
  margin: 0 0 12px;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--sideout-pink-light, #F9A8D4);
}

.v2-hero-events__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
  max-width: 600px;
}

/* Frosted-glass card: the hero photo behind shows through a 10%-white panel
   blurred 12px, with a 1px white/20% border + 16px corner. Mirrors the
   Lovable mockup the client signed off on. */
.v2-hero-events__card {
  position: relative;
  /* Cap width so a lone event card doesn't stretch to the full 600px grid.
     The grid uses auto-fit + minmax(260px, 1fr), so a single card would
     otherwise expand to 100% width. Multi-card rows are unaffected — their
     cells are already narrower than this cap. (Rick: a single event
     shouldn't render at 100% width.) */
  max-width: 340px;
  display: flex;
  align-items: stretch;
  gap: 16px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.10);
  border: 1px solid rgba(255, 255, 255, 0.20);
  border-radius: 16px;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  color: #ffffff;
  text-decoration: none;
  cursor: help;
  transition: background 200ms cubic-bezier(0.22, 1, 0.36, 1);
}
.v2-hero-events__card:hover {
  background: rgba(255, 255, 255, 0.15);
}

/* Past events read as a heavier glass panel rather than a faded card: a
   light vertical gradient over a stronger blur+saturate gives the surface
   real depth against the hero photo. Inner top highlight + a soft outer
   shadow sells the "frosted glass" feel. Opacity is only barely below 1 so
   text stays fully legible; the Directions link still resolves to Google
   Maps for visitors who want to scout the venue. */
.v2-hero-events__card--past {
  opacity: 0.95;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.22) 0%, rgba(255, 255, 255, 0.12) 100%);
  border-color: rgba(255, 255, 255, 0.40);
  backdrop-filter: blur(20px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.30),
    inset 0 -1px 0 rgba(255, 255, 255, 0.08),
    0 8px 24px -8px rgba(29, 37, 73, 0.30);
}
.v2-hero-events__card--past:hover {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.22) 0%, rgba(255, 255, 255, 0.12) 100%);
}
.v2-hero-events__card--past .v2-hero-events__card-month {
  color: rgba(255, 255, 255, 0.85);
}

.v2-hero-events__card-date {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 56px;
}
.v2-hero-events__card-month {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.70);
  line-height: 16px;
}
.v2-hero-events__card-day {
  margin-top: 4px;
  font-size: 30px;
  font-weight: 800;
  line-height: 36px;
  color: #ffffff;
}

.v2-hero-events__card-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  flex: 1 1 auto;
}
.v2-hero-events__card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.v2-hero-events__card-time {
  margin-top: 4px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: rgba(255, 255, 255, 0.80);
}
.v2-hero-events__card-dirs {
  margin-top: 4px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: var(--sideout-pink-light, #F9A8D4);
  align-self: flex-start;
}
.v2-hero-events__icon { flex: 0 0 auto; width: 14px; height: 14px; }

.v2-hero-events__more {
  display: inline-block;
  margin-top: 10px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.35);
}
.v2-hero-events__more:hover {
  color: #ffffff;
  border-bottom-color: rgba(255, 255, 255, 0.7);
}

@media (max-width: 640px) {
  .v2-hero-events__grid { grid-template-columns: 1fr; }
}
</style>

<style>
/* Unscoped: the tooltip is teleported to <body>, outside this component's
   scoped-CSS data-attribute, so its rules need to live in a global block.
   The .v2-hero-events-tip prefix is specific enough to be safe globally. */
.v2-hero-events-tip {
  position: absolute;
  /* Sit just BELOW TopHeaderV2's sticky bar (Tailwind z-10) so that, as the
     anchored tip scrolls up with its hero card, it slides UNDER the header
     instead of over it. Still well above the hero content, which is
     non-positioned (z-auto), so the tip stays visible over the cards. The
     header and this body-teleported tip share the root stacking context
     (#app creates none on the v2 layout), so these values compare directly. */
  z-index: 9;
  min-width: 220px;
  max-width: 300px;
  padding: 13px 16px 14px 18px;
  /* Inverted (light) frosted-glass panel — reads as a clean white card lifted
     off the dark hero photo, instead of the old navy chip. Kept the pink
     left-rail + underline accent (the one V2 embellishment that's part of the
     system) so it still ties to the brand. */
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid var(--sideout-border, #E0E4EB);
  border-left: 3px solid var(--sideout-pink, #DA1884);
  border-radius: 10px;
  backdrop-filter: blur(20px) saturate(140%);
  -webkit-backdrop-filter: blur(20px) saturate(140%);
  box-shadow:
    0 18px 40px -12px rgba(29, 37, 73, 0.28),
    0 4px 12px -4px rgba(29, 37, 73, 0.12);
  color: var(--sideout-navy, #1D2549);
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-size: 14px;
  line-height: 20px;
  pointer-events: none;
  opacity: 0;
  transform: translateY(-4px);
  transition:
    opacity 160ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}
.v2-hero-events-tip.is-open {
  opacity: 1;
  transform: translateY(0);
}
.v2-hero-events-tip__title {
  position: relative;
  margin: 0;
  padding-bottom: 7px;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  letter-spacing: -0.005em;
  color: var(--sideout-navy, #1D2549);
}
.v2-hero-events-tip__title::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 20px;
  height: 1px;
  background: var(--sideout-pink, #DA1884);
}
.v2-hero-events-tip__date {
  margin: 9px 0 0;
  font-size: 11px;
  font-weight: 600;
  line-height: 14px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--sideout-pink, #DA1884);
}
.v2-hero-events-tip__venue {
  margin: 5px 0 0;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.005em;
  color: var(--sideout-muted, #626884);
}
</style>
